/**
 * Instance Status Badge Component
 *
 * Reusable status badge for Railway instance states.
 */

import { LitElement, html, css } from "lit";
import { customElement, property } from "lit/decorators.js";
import { raxTheme } from "../styles/rax-theme.js";

export type InstanceStatus = "provisioning" | "running" | "sleeping" | "stopped" | "error";

@customElement("instance-status-badge")
export class InstanceStatusBadge extends LitElement {
  static override styles = [
    raxTheme,
    css`
      :host {
        display: inline-block;
      }
      
      .badge {
        display: inline-flex;
        align-items: center;
        gap: var(--space-1);
        padding: var(--space-1) var(--space-2);
        border-radius: var(--radius-full);
        font-size: 10px;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }
      
      .badge.provisioning {
        background: rgba(245, 158, 11, 0.2);
        color: var(--warning);
      }
      
      .badge.running {
        background: rgba(16, 185, 129, 0.2);
        color: var(--success);
      }
      
      .badge.sleeping {
        background: rgba(99, 102, 241, 0.2);
        color: #818cf8;
      }
      
      .badge.stopped {
        background: var(--bg-hover);
        color: var(--text-tertiary);
      }
      
      .badge.error {
        background: rgba(239, 68, 68, 0.2);
        color: var(--error);
      }
      
      .spinner {
        width: 10px;
        height: 10px;
        border: 2px solid currentColor;
        border-top-color: transparent;
        border-radius: 50%;
        animation: spin 1s linear infinite;
      }
      
      @keyframes spin {
        from {
          transform: rotate(0deg);
        }
        to {
          transform: rotate(360deg);
        }
      }
      
      .dot {
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: currentColor;
      }
      
      .icon {
        font-size: 10px;
      }
    `,
  ];

  @property({ type: String })
  status: InstanceStatus = "stopped";

  override render() {
    return html`
      <span class="badge ${this.status}">
        ${this.renderIcon()}
        ${this.renderText()}
      </span>
    `;
  }

  private renderIcon() {
    switch (this.status) {
      case "provisioning":
        return html`
          <span class="spinner"></span>
        `;
      case "running":
        return html`
          <span class="dot"></span>
        `;
      case "sleeping":
        return html`
          <span class="icon">💤</span>
        `;
      case "error":
        return html`
          <span class="icon">✕</span>
        `;
      case "stopped":
      default:
        return html`
          <span class="dot"></span>
        `;
    }
  }

  private renderText() {
    switch (this.status) {
      case "provisioning":
        return "Provisioning...";
      case "running":
        return "Running";
      case "sleeping":
        return "Sleeping";
      case "error":
        return "Error";
      case "stopped":
      default:
        return "Stopped";
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "instance-status-badge": InstanceStatusBadge;
  }
}
